import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Loader2, FileText, Printer } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DocsPage = () => {
  const [md, setMd] = useState('');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const path = window.location.pathname;
  const isBusiness = path.endsWith('/business');
  const isLaunch = path.endsWith('/launch-plan');
  const isTech = path.endsWith('/tech');
  const isBRD = path.endsWith('/brd');
  const isBlueprint = path.endsWith('/blueprint');

  useEffect(() => {
    const apiPath = isBlueprint ? '/public/blueprint'
      : isBRD ? '/public/brd'
      : isTech ? '/public/tech-stack'
      : isLaunch ? '/public/launch-plan'
      : isBusiness ? '/public/business-plan'
      : '/public/feature-guide';
    axios.get(`${API}${apiPath}`)
      .then((r) => setMd(r.data?.markdown || ''))
      .catch((e) => setErr(e.response?.data?.detail || 'Could not load the document'))
      .finally(() => setLoading(false));
  }, [isBusiness, isLaunch, isTech, isBRD, isBlueprint]);

  const title = isBlueprint ? 'Blueprint'
    : isBRD ? 'BRD'
    : isTech ? 'Tech Stack'
    : isLaunch ? 'Launch Plan'
    : isBusiness ? 'Business Plan'
    : 'Feature Guide';
  const otherLinks = [
    { path: '/docs', label: 'Feature Guide', active: !isBusiness && !isLaunch && !isTech && !isBRD && !isBlueprint },
    { path: '/docs/blueprint', label: 'Blueprint', active: isBlueprint },
    { path: '/docs/brd', label: 'BRD', active: isBRD },
    { path: '/docs/business', label: 'Business Plan', active: isBusiness },
    { path: '/docs/launch-plan', label: 'Launch Plan', active: isLaunch },
    { path: '/docs/tech', label: 'Tech Stack', active: isTech },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10 print:hidden">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText size={20} className="text-purple-600" />
            <h1 className="font-bold text-gray-900">
              MyClassAI — {title}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden md:flex items-center gap-2 text-xs">
              {otherLinks.filter(l => !l.active).map(l => (
                <a key={l.path} href={l.path} className="text-purple-700 hover:text-purple-900 underline">
                  {l.label}
                </a>
              ))}
            </div>
            <button
              onClick={() => window.print()}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700"
              data-testid="docs-print-btn"
            >
              <Printer size={14} /> Print
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10">
        {loading && (
          <div className="flex items-center gap-3 text-gray-500 p-10 justify-center">
            <Loader2 className="animate-spin" size={20} />
            Loading guide…
          </div>
        )}
        {err && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4">
            {err}
          </div>
        )}
        {!loading && !err && (
          <article
            className="prose prose-slate max-w-none
                       prose-headings:scroll-mt-20 prose-h1:text-3xl prose-h1:font-bold
                       prose-h2:text-2xl prose-h2:font-semibold prose-h2:mt-12 prose-h2:border-b prose-h2:pb-2
                       prose-h3:text-xl prose-h3:font-semibold prose-h3:mt-8
                       prose-table:text-sm prose-th:bg-slate-100 prose-th:font-semibold
                       prose-blockquote:border-l-purple-500 prose-blockquote:bg-purple-50 prose-blockquote:py-1 prose-blockquote:px-3 prose-blockquote:not-italic
                       prose-code:bg-slate-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-pink-700
                       prose-a:text-purple-600"
            data-testid="docs-content"
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{md}</ReactMarkdown>
          </article>
        )}
      </main>

      <footer className="text-center text-xs text-gray-400 pb-8 print:hidden">
        © MyClassAI · For school admins, teachers, parents and partners
      </footer>
    </div>
  );
};

export default DocsPage;
